/* eslint-disable react-hooks/exhaustive-deps */
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Grid } from '@mui/material';
// Other libraries
import { isEmpty } from 'lodash';
// Form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { updateError } from '../../redux/slices/user';
import { getUserDropdownValue, getUserId, practiceUserData } from '../../utils/clinCheckUtils';
import { FormProvider } from '../hook-form';
import { useAuth } from '../../hooks';
// Redux
import { getCurrentTreatmentIdV2, handleClinCheckInProgressStatus, handleClinCheckUploadedStatus } from '../../redux/slices/clincheck';
import { dispatch, useSelector } from '../../redux/store';
// Validation
import { ClinCheckUploadedFormSchema } from '../../validations/validations';
// Style
import { appointmentGridStyle } from '../../styles/DialogueTabs';
import UserSearchDropdownSingleSelect from '../UserSearchDropdownSingleSelect';
import ClinCheckDatePicker from './ClinCheckDatePicker';

const ClinCheckUploadedForm = forwardRef(({ handleClose, currentTab, leadDetail }, ref) => {
    const { followLead } = useSelector((state) => state.lead);
    const { practiceUser } = useSelector((state) => state.practiceUser);
    const { clinCheckListing } = useSelector((state) => state.clinCheck);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { user } = useAuth();

    const defaultValues = {
        date_uploaded: null,
        who_uploaded: '',
        is_task_done: false,
    };

    const currentUser = practiceUser.results?.find((e) => e?.user?.id === user?.pk);

    useEffect(() => {
        if (!isEmpty(practiceUserData(practiceUser)?.filter((ele) => ele?.user?.id === currentUser?.user?.id))) {
            setValue('who_uploaded', getUserDropdownValue(currentUser));
        }
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            async submit() {
                await handleSubmit((data) => onSubmit(data))();
            },
        }),
        []
    );

    const methods = useForm({
        resolver: yupResolver(ClinCheckUploadedFormSchema),
        defaultValues,
    });

    const {
        control,
        setValue,
        reset,
        handleSubmit,
    } = methods;

    const onSubmit = (values) => {
        reset();
        if (clinCheckListing?.results?.[0]?.status === 'UPLOADED') {
            handleClose('finish');
            return;
        }
        if (isEmpty(currentTab)) {
            dispatch(updateError({
                message: 'Please select tab',
                status: false
            }));
            return;
        }
        if (!isEmpty(clinCheckListing?.results)) {
            handleUpdateStatus({ clinCheckId: clinCheckListing?.results?.[0]?.id, payload: values });
        } else {
            const data = {
                leadId: followLead?.id || leadDetail?.lead_id || leadDetail?.id,
                treatmentId: getCurrentTreatmentIdV2(followLead?.lead_treatments?.[0]?.id || leadDetail?.treatment_id || leadDetail?.lead_treatments?.[0]?.id),
                practiceId,
                payload: values
            };
            dispatch(handleClinCheckInProgressStatus(data, handleUpdateStatus));
        }
    };

    const handleUpdateStatus = (payload) => {
        const data = {
            leadId: followLead?.id || leadDetail?.lead_id || leadDetail?.id,
            treatmentId: getCurrentTreatmentIdV2(followLead?.lead_treatments?.[0]?.id || leadDetail?.treatment_id || leadDetail?.lead_treatments?.[0]?.id),
            clinCheckId: payload?.clinCheckId,
            practiceId,
            payload: {
                recorded_at: moment(payload?.payload?.date_uploaded).format('YYYY-MM-DDTHH:mm:ss.sssZ'),
                recorded_by: getUserId(practiceUser, payload?.payload?.who_uploaded),
                note: ''
            }
        };
        dispatch(handleClinCheckUploadedStatus(data, handleClose));
    };

    const handlePracticeUserChange = (e, newValue) => {
        if (newValue === null) {
            setValue('who_uploaded', '', { shouldValidate: true });
            return;
        };
        setValue('who_uploaded', newValue, { shouldValidate: true });
    };

    return <div>
        <FormProvider methods={methods}>
            <Grid sx={appointmentGridStyle}>
                <ClinCheckDatePicker name={'date_uploaded'} label={'Date uploaded'} control={control} />
                <UserSearchDropdownSingleSelect name={'who_uploaded'} control={control} onChange={handlePracticeUserChange} label='Who uploaded' />
            </Grid>
        </FormProvider>
    </div>;
});

export default ClinCheckUploadedForm;

ClinCheckUploadedForm.propTypes = {
    handleClose: PropTypes.func,
    currentTab: PropTypes.string,
    leadDetail: PropTypes.object,
};